import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

export default function SalesByDay() {
  const { user ,companyId} = useAuth();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [companySnap, setCompanySnap] = useState({});
  const perPage = 10;
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };

  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') {
      return ts.toDate();
    }
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const dateKey = (d) => {
    const pad = (v) => String(v).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

const loadPlan = async () => {
  const docRef = doc(db, 'companies', companyId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    setPlan(docSnap.data()?.subscription?.plan || 'Free Trial');
  }
};

  useEffect(() => {
    loadPlan();
    // Set today's date as default for both from and to date
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const loadSales = async () => {
    if (!user || !companyId) return;
    try {
      const q = query(
        collection(db, 'companies', companyId, 'orders'),
        where('paymentStatus', '!=', 'Void'),
        orderBy('timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const salesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Group by date
      const grouped = salesData.reduce((acc, sale) => {
        const ts = parseTimestamp(sale.timestamp);
        if (!ts) return acc;
        const date = dateKey(ts);
        const saleTotal = toNumber(sale.total);
        if (!acc[date]) {
          acc[date] = {
            date: date,
            total: 0,
            count: 0
          };
        }
        acc[date].total += saleTotal;
        acc[date].count++;
        return acc;
      }, {});

      setSales(Object.values(grouped));
    } catch (error) {
      console.error('Error loading sales:', error);
    }
  };

  const loadCompanyName = async () => {
    if (!user || !companyId) return;
    const snap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
    if (snap.exists()) setCompanySnap(snap.data());
  };

  const filtered = sales.filter((item) => {
    const itemDate = new Date(item.date);
    const from = fromDate ? new Date(fromDate) : new Date(0);
    const to = toDate ? new Date(toDate) : new Date();
    
    // Set to to end of day
    to.setHours(23, 59, 59, 999);

    const matchesDate = itemDate >= from && itemDate <= to;
    const matchesSearch = search
      ? formatDate(item.date).includes(search) || item.date.includes(search)
      : true;
    return matchesDate && matchesSearch;
  });

  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  const chartData = {
    labels: filtered.map(f => f.date),
    datasets: [
      {
        label: 'Total Sales (RM)',
        data: filtered.map(f => toNumber(f.total)),
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139, 92, 246, 0.2)',
        tension: 0.2,
        pointRadius: 3,
        pointBorderWidth: 2,
        pointBackgroundColor: '#8b5cf6',
        pointBorderColor: '#c4b5fd',
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `RM ${toNumber(ctx.parsed.y).toFixed(2)}`,
        },
        displayColors: false,
        titleColor: '#0b0f19',
        bodyColor: '#0b0f19',
        backgroundColor: '#f9fafb',
        borderColor: '#0b0f19',
        borderWidth: 1,
      },
      datalabels: { display: false },
    },
    layout: {
      padding: { left: 0, right: 0, top: 4, bottom: 0 },
    },
    scales: {
      x: {
        ticks: { color: '#e5e7eb', autoSkip: true, maxTicksLimit: 12, maxRotation: 35, minRotation: 25, callback: (val, idx) => filtered[idx]?.date },
        grid: { color: 'rgba(229,231,235,0.15)' },
      },
      y: {
        ticks: {
          color: '#e5e7eb',
          callback: (v) => `RM ${toNumber(v).toFixed(2)}`,
        },
        grid: { color: 'rgba(229,231,235,0.15)' },
        beginAtZero: true,
      },
    },
  };

  const exportToPDF = () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }
  
    const docPDF = new jsPDF('p', 'mm', 'a4');
    const title = `Sales By Day Report (${formatDate(fromDate)} - ${formatDate(toDate)})`;

    docPDF.setFontSize(12);
    docPDF.text(companySnap.companyName || 'Company', 105, 10, { align: 'center' });
    docPDF.setFontSize(10);
    docPDF.text(title, 105, 16, { align: 'center' });

    // Add chart image if available
    try {
      const chartCanvas = document.querySelector('.sales-by-day-chart canvas');
      if (chartCanvas) {
        const imgData = chartCanvas.toDataURL('image/png', 1.0);
        const pageWidth = docPDF.internal.pageSize.getWidth() - 20;
        const imgHeight = 80;
        docPDF.addImage(imgData, 'PNG', 10, 20, pageWidth, imgHeight);
      }
    } catch (err) {
      console.warn('Could not render chart to PDF:', err);
    }

    autoTable(docPDF, {
      head: [['Date', 'Total Sales']],
      body: filtered.map(row => [formatDate(row.date), `RM${row.total.toFixed(2)}`]),
      startY: 105,
      styles: { fontSize: 9 },
    });

    docPDF.setFontSize(10);
    docPDF.text(`Total Sales: RM${filtered.reduce((sum, day) => sum + day.total, 0).toFixed(2)}`, 105, docPDF.lastAutoTable.finalY + 10, { align: 'center' });

    const pageHeight = docPDF.internal.pageSize.height;
    docPDF.setFontSize(9);
    docPDF.text('Powered by CashMate', 105, pageHeight - 10, { align: 'center' });

    const file = `sales-by-day_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`.replace(/\//g, '-');
    docPDF.save(file);
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadSales(), loadCompanyName()]);
      setLoading(false);
    };
    loadAll();
  }, [user,companyId]);

  if (loading) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading information...</span>
      </div>
    );
  }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Sales By Day</h2>

      <div className="flex gap-4 mb-6">
        <div className="flex gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
          />
        </div>
        <input
          type="text"
          placeholder="Search by date"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
        />
        <button onClick={exportToPDF} className="px-3 py-2 bg-purple-600 rounded text-white">Export to PDF</button>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg p-4 mb-6 sales-by-day-chart">
        <h3 className="text-sm text-gray-300 mb-2">Daily Sales Trend</h3>
        <div className="h-72 w-full">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>

      <div className="text-lg font-semibold text-white mb-4">
        Total Sales: <span className="text-green-400">RM {filtered.reduce((sum, day) => sum + day.total, 0).toFixed(2)}</span>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((item, i) => (
              <tr key={i} className="border-t border-gray-700">
                <td className="px-4 py-2">{formatDate(item.date)}</td>
                <td className="px-4 py-2 font-bold">RM {item.total.toFixed(2)}</td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-gray-400" colSpan={2}>No data found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center flex-wrap mt-6 gap-2">
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`px-4 py-2 rounded ${currentPage === i + 1 ? 'bg-purple-600 text-white' : 'bg-[#2c2c2c] text-gray-300'}`}
            onClick={() => setCurrentPage(i + 1)}
          >{i + 1}</button>
        ))}
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}
